/**
 * Promise-returning helpers built on the `Modal` primitive — drop-in
 * replacements for `window.prompt` / `window.confirm` so the apps can keep
 * their visual identity inside dialogs. The native browser modals don't
 * respect our theming, focus rings, or i18n, and they look out of place inside
 * a canvas-driven game shell.
 */

import { el } from "./components/dom";
import { openModal } from "./components/Modal";

export interface PromptInputOptions {
  readonly title: string;
  readonly label?: string;
  readonly defaultValue?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /** Return a non-empty error message to block submit, or null to accept. */
  readonly validate?: (value: string) => string | null;
}

export interface ConfirmDialogOptions {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /** When true, the primary action is styled as destructive. */
  readonly danger?: boolean;
}

/**
 * Prompt the user for a string. Resolves to the trimmed value on confirm, or
 * `null` on cancel / Escape / overlay dismiss.
 */
export function promptInput(opts: PromptInputOptions): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: string | null): void => {
      if (settled) return;
      settled = true;
      handle.close();
      resolve(value);
    };

    const errorNode = el("div", { class: "modal-input-error" });
    const input = el("input", {
      class: "modal-input",
      attrs: { type: "text", value: opts.defaultValue ?? "" },
    }) as HTMLInputElement;
    if (opts.defaultValue !== undefined) input.value = opts.defaultValue;

    const body = el("div", { class: "modal-input-wrap" }, [
      opts.label ? el("label", { class: "modal-input-label", text: opts.label }) : null,
      input,
      errorNode,
    ]);

    function attemptConfirm(): void {
      const raw = input.value.trim();
      const error = opts.validate ? opts.validate(raw) : null;
      if (error !== null && error !== undefined && error !== "") {
        errorNode.textContent = error;
        input.focus();
        return;
      }
      settle(raw);
    }

    input.addEventListener("keydown", (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key === "Enter") {
        e.preventDefault();
        attemptConfirm();
      } else if (ke.key === "Escape") {
        e.preventDefault();
        settle(null);
      }
    });

    const handle = openModal({
      title: opts.title,
      body,
      actions: [
        { label: opts.cancelLabel ?? "Cancel", onClick: () => settle(null) },
        { label: opts.confirmLabel ?? "OK", primary: true, onClick: attemptConfirm },
      ],
    });

    // Focus the input first so typing flows; primary-button focus is the
    // confirm-dialog convention, but a prompt's whole point is text entry.
    queueMicrotask(() => input.focus());
  });
}

/**
 * Yes/no confirmation. Resolves `true` on confirm, `false` on cancel /
 * Escape / overlay dismiss. The confirm button gets initial focus so an
 * accidental Enter does not destructively act — that's the prompt's job.
 */
export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: boolean): void => {
      if (settled) return;
      settled = true;
      cleanup();
      handle.close();
      resolve(value);
    };

    function onKeydown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        settle(false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        settle(true);
      }
    }

    function cleanup(): void {
      document.removeEventListener("keydown", onKeydown);
    }

    document.addEventListener("keydown", onKeydown);

    const handle = openModal({
      title: opts.title,
      body: opts.message,
      actions: [
        { label: opts.cancelLabel ?? "Cancel", onClick: () => settle(false) },
        {
          label: opts.confirmLabel ?? "OK",
          primary: true,
          onClick: () => settle(true),
        },
      ],
    });

    // Focus the primary confirm button so screen readers + keyboard users land
    // on the action they're being asked about.
    queueMicrotask(() => {
      const primary = document.querySelector<HTMLButtonElement>(
        ".modal-actions .crest-btn--primary",
      );
      primary?.focus();
    });
  });
}
